import {
  CallbackProperty,
  BoundingSphere,
  Cartesian2,
  Cartesian3,
  Cartographic,
  Color,
  KeyboardEventModifier,
  Matrix4,
  Math as CesiumMath,
  PropertyBag,
  ScreenSpaceEventHandler,
  ScreenSpaceEventType,
  Transforms,
  defined,
} from "cesium";

import { createEdgeAutoPan } from "./EdgeAutoPan";
import { computeZoomLevel } from "../MapUtility";

export function createDigitizerAssistant({
  viewer,
  dataSource,
  getMinDistance,
  getMaxDistance,
  getDuration,
  onStop,
  getSmoothness, // Optional: () => number in [0..1] (0=straighter, 1=curvier)
  getCurvePointSpacing, // Optional: () => meters (>0) (smaller = higher resolution)
}) {
  let stopped = false;

  const scene = viewer.scene;
  const canvas = scene.canvas;
  const handler = new ScreenSpaceEventHandler(canvas);

  const edgeAutoPan = createEdgeAutoPan({
    viewer,

    // short English comment: same defaults as current DigitizerAssistant
    edgePx: 22,
    strengthMin: 0.15,
    strengthMax: 1.0,

    // short English comment: keep your previous clamps
    speedMin: 5,
    speedMax: 60,

    // short English comment: keep your previous profile (customizable)
    speedZGt24: 5,
    speedZ22_24: 20,
    speedZLt22: 50,

    getZoomLevel: () => computeZoomLevel(viewer),
  });

  const entities = dataSource?.entities ?? viewer.entities;

  const positions = [];
  const tempPoints = [];
  let tempLineEntity = null;

  let lastHeadingRad = null;

  // Anti double-click state
  let lastLeftClickTime = 0;
  // Quick edit jump distances (EE)
  let overrideMinDistance = null;
  let overrideMaxDistance = null;

  let jumpEditorEl = null;
  let jumpEditorOpen = false;

  let lastEKeyTime = 0;
  const DOUBLE_E_MS = 350;

  const lastLeftClickScreen = new Cartesian2();
  const DOUBLE_CLICK_MS = 300;
  const DOUBLE_CLICK_PX = 6;

  // Curve-select mode (hold Shift)
  let shiftDown = false;
  const selectedPointIds = new Set(); // entity id
  const indexByPointId = new Map(); // entity id -> positions index
  const PICK_MAX_PX = 14;

  const scratch = {
    frame: new Matrix4(),
    invFrame: new Matrix4(),

    v1w: new Cartesian3(),
    v2w: new Cartesian3(),
    v1l: new Cartesian3(),
    v2l: new Cartesian3(),

    segDelta: new Cartesian3(),
    segDirWorld: new Cartesian3(),
    segDirLocal: new Cartesian3(),

    camSurface: new Cartesian3(),
    camCarto: new Cartographic(),

    offsetLocal: new Cartesian3(),
    offsetWorld: new Cartesian3(),
    destSurface: new Cartesian3(),
    destCarto: new Cartographic(),
    dest: new Cartesian3(),

    w2: new Cartesian2(),
    sp: new Cartesian2(),
    sp2: new Cartesian2(),
  };

  const clamp01 = (v) => Math.max(0, Math.min(1, v));

  const closeJumpEditor = () => {
    if (!jumpEditorEl) return;
    jumpEditorEl.style.display = "none";
    jumpEditorOpen = false;
  };

  const openJumpEditor = () => {
    if (!jumpEditorEl) {
      jumpEditorEl = document.createElement("div");
      jumpEditorEl.style.position = "absolute";
      jumpEditorEl.style.zIndex = "99999";
      jumpEditorEl.style.minWidth = "260px";
      jumpEditorEl.style.padding = "10px";
      jumpEditorEl.style.border = "1px solid #000";
      jumpEditorEl.style.borderRadius = "8px";
      jumpEditorEl.style.background = "#fff";
      jumpEditorEl.style.color = "#000";
      jumpEditorEl.style.fontSize = "12px";
      jumpEditorEl.style.boxShadow = "0 10px 30px rgba(0,0,0,0.15)";

      jumpEditorEl.innerHTML = `
      <div style="font-weight:600;margin-bottom:8px;color:#000;">Edit jump distances</div>

      <div style="display:flex;gap:8px;align-items:center;margin-bottom:8px;">
        <label style="width:90px;color:#000;">Small</label>
        <input id="da_jump_min" type="number" step="0.1"
          style="flex:1;padding:6px;border-radius:6px;border:1px solid #000;background:#fff;color:#000;outline:none;" />
      </div>

      <div style="display:flex;gap:8px;align-items:center;margin-bottom:10px;">
        <label style="width:90px;color:#000;">Large</label>
        <input id="da_jump_max" type="number" step="0.1"
          style="flex:1;padding:6px;border-radius:6px;border:1px solid #000;background:#fff;color:#000;outline:none;" />
      </div>

      <div style="display:flex;gap:8px;justify-content:flex-end;">
        <button id="da_jump_cancel"
          style="padding:6px 10px;border-radius:6px;border:1px solid #000;background:#fff;color:#000;cursor:pointer;">
          Cancel
        </button>
        <button id="da_jump_save"
          style="padding:6px 10px;border-radius:6px;border:1px solid #000;background:#fff;color:#000;cursor:pointer;font-weight:600;">
          Save
        </button>
      </div>
    `;

      const parent = canvas?.parentElement ?? document.body;
      parent.appendChild(jumpEditorEl);

      const minInput = jumpEditorEl.querySelector("#da_jump_min");
      const maxInput = jumpEditorEl.querySelector("#da_jump_max");
      const btnSave = jumpEditorEl.querySelector("#da_jump_save");
      const btnCancel = jumpEditorEl.querySelector("#da_jump_cancel");

      const doSave = () => {
        const minV = Number(minInput?.value);
        const maxV = Number(maxInput?.value);

        if (Number.isFinite(minV)) overrideMinDistance = Math.max(0, minV);
        if (Number.isFinite(maxV)) overrideMaxDistance = Math.max(0, maxV);

        closeJumpEditor();
      };

      btnSave?.addEventListener("click", (ev) => {
        ev.preventDefault();
        ev.stopPropagation();
        doSave();
      });

      btnCancel?.addEventListener("click", (ev) => {
        ev.preventDefault();
        ev.stopPropagation();
        closeJumpEditor();
      });

      jumpEditorEl.addEventListener("keydown", (ev) => {
        if (ev.key === "Enter") {
          ev.preventDefault();
          ev.stopPropagation();
          doSave();
        }
        if (ev.key === "Escape") {
          ev.preventDefault();
          ev.stopPropagation();
          closeJumpEditor();
        }
      });
    }

    // Position near canvas center
    const rect = canvas.getBoundingClientRect();
    jumpEditorEl.style.left = `${Math.round(rect.left + rect.width / 2 - 140)}px`;
    jumpEditorEl.style.top = `${Math.round(rect.top + rect.height / 2 - 90)}px`;
    jumpEditorEl.style.display = "block";
    jumpEditorOpen = true;

    const { min, max } = getDistanceRange();
    const minInput = jumpEditorEl.querySelector("#da_jump_min");
    const maxInput = jumpEditorEl.querySelector("#da_jump_max");

    if (minInput) minInput.value = String(min);
    if (maxInput) maxInput.value = String(max);

    // Focus large jump immediately
    if (maxInput) {
      maxInput.focus();
      maxInput.select?.();
    }
  };

  const getSmoothness01 = () => {
    const v = Number(getSmoothness?.());
    if (!Number.isFinite(v)) return 0.75;
    return clamp01(v);
  };

  const getCurveSpacingMeters = (ptsWorld) => {
    let s = Number(getCurvePointSpacing?.());
    if (Number.isFinite(s) && s > 0) return Math.max(0.25, Math.min(100, s));

    if (!Array.isArray(ptsWorld) || ptsWorld.length < 2) return 2;

    let sum = 0;
    let cnt = 0;
    for (let i = 0; i < ptsWorld.length - 1; i++) {
      const d = Cartesian3.distance(ptsWorld[i], ptsWorld[i + 1]);
      if (Number.isFinite(d) && d > 0) {
        sum += d;
        cnt++;
      }
    }
    if (cnt <= 0) return 2;

    const avg = sum / cnt;
    return Math.max(0.5, Math.min(20, avg / 6));
  };

  const getDistanceRange = () => {
    const minRaw = overrideMinDistance != null ? overrideMinDistance : getMinDistance?.();
    const maxRaw = overrideMaxDistance != null ? overrideMaxDistance : getMaxDistance?.();

    let min = Number(minRaw ?? 0);
    let max = Number(maxRaw ?? 0);

    if (!Number.isFinite(min)) min = 0;
    if (!Number.isFinite(max)) max = 0;

    if (min < 0) min = 0;
    if (max < 0) max = 0;

    if (max <= 0 && min > 0) max = min;

    if (max < min) {
      const t = min;
      min = max;
      max = t;
    }

    return { min, max };
  };

  const clampScreenToCanvas = (screenPos, out) => {
    const w = canvas.clientWidth || 0;
    const h = canvas.clientHeight || 0;
    const x = Math.max(0, Math.min(Math.max(0, w - 1), screenPos.x));
    const y = Math.max(0, Math.min(Math.max(0, h - 1), screenPos.y));
    out.x = x;
    out.y = y;
    return out;
  };

  const globePickAt = (screenPos) => {
    const ray = viewer.camera.getPickRay(screenPos);
    if (!ray) return null;
    const p = scene.globe.pick(ray, scene);
    return defined(p) ? p : null;
  };

  const getMapPosition = (movement) => {
    if (!movement?.position) return null;

    const sp = clampScreenToCanvas(movement.position, scratch.sp);

    // Try depth pick first
    if (scene.pickPositionSupported) {
      const p1 = scene.pickPosition(sp);
      if (defined(p1)) return p1;
    }

    // Normal globe pick
    let p2 = globePickAt(sp);
    if (p2) return p2;

    // short English comment: edge-safe fallback by trying small insets (avoids corner misses)
    const INSET = 3;
    const w = canvas.clientWidth || 0;
    const h = canvas.clientHeight || 0;

    const tries = [
      [INSET, 0],
      [-INSET, 0],
      [0, INSET],
      [0, -INSET],
      [INSET, INSET],
      [-INSET, INSET],
      [INSET, -INSET],
      [-INSET, -INSET],
    ];

    for (let i = 0; i < tries.length; i++) {
      const dx = tries[i][0];
      const dy = tries[i][1];
      scratch.sp2.x = Math.max(0, Math.min(Math.max(0, w - 1), sp.x + dx));
      scratch.sp2.y = Math.max(0, Math.min(Math.max(0, h - 1), sp.y + dy));
      p2 = globePickAt(scratch.sp2);
      if (p2) return p2;
    }

    return null;
  };

  // short English comment: stable world->screen
  const toWin = (world, out) => {
    if (!world) return null;
    const fn = scene?.cartesianToCanvasCoordinates;
    if (typeof fn !== "function") return null;
    const v = fn.call(scene, world, out);
    if (!v) return null;
    if (!Number.isFinite(v.x) || !Number.isFinite(v.y)) return null;
    return v;
  };

  // short English comment: center-pan state
  let centerPanToken = 0;

  const centerScratch = {
    win: new Cartesian2(),
    sp0: new Cartesian2(),
    sp1: new Cartesian2(),
    sp2: new Cartesian2(),
    bs: new BoundingSphere(new Cartesian3(), 1.0),
  };

  // short English comment: pick world at screen
  const pickWorldAtScreen = (screenPos) => {
    const sp = clampScreenToCanvas(screenPos, centerScratch.sp0);

    if (scene.pickPositionSupported) {
      try {
        const p = scene.pickPosition(sp);
        if (defined(p)) return p;
      } catch (_) {}
    }

    const gp = globePickAt(sp);
    return gp || null;
  };

  // short English comment: estimate meters-per-pixel
  const estimateMppAtScreen = (screenPos) => {
    const p0 = pickWorldAtScreen(screenPos);
    if (!p0) return null;

    centerScratch.sp1.x = screenPos.x + 1;
    centerScratch.sp1.y = screenPos.y;
    const p1 = pickWorldAtScreen(centerScratch.sp1);

    centerScratch.sp2.x = screenPos.x;
    centerScratch.sp2.y = screenPos.y + 1;
    const p2 = pickWorldAtScreen(centerScratch.sp2);

    let sum = 0;
    let n = 0;

    if (p1) {
      sum += Cartesian3.distance(p0, p1);
      n++;
    }
    if (p2) {
      sum += Cartesian3.distance(p0, p2);
      n++;
    }

    if (n === 0) return null;

    const mpp = sum / n;
    return Number.isFinite(mpp) && mpp > 0 ? mpp : null;
  };

  // short English comment: center committed point smoothly, no zoom change
  const centerCameraOnCommitted = (worldPos) => {
    if (stopped) return false;
    if (!worldPos) return false;

    const w = canvas.clientWidth || scene.drawingBufferWidth || 0;
    const h = canvas.clientHeight || scene.drawingBufferHeight || 0;
    if (!(w > 0 && h > 0)) return false;

    const win = toWin(worldPos, centerScratch.win);
    if (!win) return false;

    const cx = w * 0.5;
    const cy = h * 0.5;

    const dxPxRaw = win.x - cx;
    const dyPxRaw = win.y - cy;

    if (!Number.isFinite(dxPxRaw) || !Number.isFinite(dyPxRaw)) return false;

    const DEAD_PX = 1.5;
    if (Math.abs(dxPxRaw) <= DEAD_PX && Math.abs(dyPxRaw) <= DEAD_PX) return true;

    const CLAMP_PX = 2000;
    const dxPx = Math.max(-CLAMP_PX, Math.min(CLAMP_PX, dxPxRaw));
    const dyPx = Math.max(-CLAMP_PX, Math.min(CLAMP_PX, dyPxRaw));

    let mpp = null;
    try {
      centerScratch.bs.center = worldPos;
      centerScratch.bs.radius = 1.0;
      mpp = viewer.camera.getPixelSize(centerScratch.bs, w, h);
    } catch (_) {
      mpp = null;
    }

    if (!(Number.isFinite(mpp) && mpp > 0)) {
      mpp = estimateMppAtScreen(win);
    }
    if (!(Number.isFinite(mpp) && mpp > 0)) return false;

    const rightMeters = dxPx * mpp;
    const upMeters = -dyPx * mpp;

    const token = ++centerPanToken;

    const durMsRaw = Number(getDuration?.() ?? 160);
    const durMs = Number.isFinite(durMsRaw) ? Math.max(60, Math.min(320, durMsRaw)) : 160;

    const t0 = typeof performance !== "undefined" && performance.now ? performance.now() : Date.now();

    viewer.camera.cancelFlight();

    let movedR = 0;
    let movedU = 0;

    const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
    const raf = typeof requestAnimationFrame === "function" ? requestAnimationFrame : (fn) => setTimeout(fn, 16);

    const tick = () => {
      if (stopped) return;
      if (token !== centerPanToken) return;

      const now = typeof performance !== "undefined" && performance.now ? performance.now() : Date.now();
      const t = Math.min(1, (now - t0) / durMs);
      const k = easeOutCubic(t);

      const wantR = rightMeters * k;
      const wantU = upMeters * k;

      const dR = wantR - movedR;
      const dU = wantU - movedU;

      if (Number.isFinite(dR) && dR !== 0) viewer.camera.moveRight(dR);
      if (Number.isFinite(dU) && dU !== 0) viewer.camera.moveUp(dU);

      movedR = wantR;
      movedU = wantU;

      try {
        scene.requestRender?.();
      } catch (_) {}

      if (t < 1) raf(tick);
    };

    raf(tick);
    return true;
  };


  // short English comment: tag our entities as helpers to avoid conflicts with other editors
  const makeHelperProps = () => new PropertyBag({ isDrawingTemp: true });

  const makeLinePositionsCb = () => new CallbackProperty(() => (positions.length >= 2 ? positions.slice() : []), false);

  let linePositionsCb = makeLinePositionsCb();

  const ensureTempLine = () => {
    if (tempLineEntity) return;

    linePositionsCb = makeLinePositionsCb();

    tempLineEntity = entities.add({
      polyline: {
        positions: linePositionsCb,
        width: 3,
        material: Color.YELLOW,
        clampToGround: true,
      },
      properties: makeHelperProps(),
    });
  };

  const refreshTempLineBinding = () => {
    if (!tempLineEntity?.polyline) return;
    linePositionsCb = makeLinePositionsCb();
    tempLineEntity.polyline.positions = linePositionsCb;
    tempLineEntity.polyline.material = Color.YELLOW;
    tempLineEntity.polyline.width = 3;
  };

  const rebuildPointIndexMap = () => {
    indexByPointId.clear();
    for (let i = 0; i < tempPoints.length; i++) {
      const e = tempPoints[i];
      if (e?.id != null) indexByPointId.set(e.id, i);
    }
  };

  const setPointSelectedVisual = (entity, selected) => {
    if (!entity?.point) return;
    entity.point.color = selected ? Color.CYAN : Color.YELLOW;
    entity.point.pixelSize = selected ? 12 : 10;
  };

  const syncSelectionVisuals = () => {
    for (let i = 0; i < tempPoints.length; i++) {
      const e = tempPoints[i];
      const sel = e?.id != null && selectedPointIds.has(e.id);
      setPointSelectedVisual(e, sel);
    }
  };

  const clearSelection = () => {
    selectedPointIds.clear();
    syncSelectionVisuals();
  };

  const rebuildAllPointEntities = () => {
    for (const p of tempPoints) entities.remove(p);
    tempPoints.length = 0;

    for (let i = 0; i < positions.length; i++) {
      const pos = positions[i];
      const pt = entities.add({
        position: pos,
        point: {
          pixelSize: 10,
          color: Color.YELLOW,
          outlineColor: Color.BLACK,
          outlineWidth: 1,
          disableDepthTestDistance: Number.POSITIVE_INFINITY,
        },
        properties: makeHelperProps(),
      });
      tempPoints.push(pt);
    }

    rebuildPointIndexMap();
    clearSelection();
  };

  const undoLastPoint = () => {
    if (positions.length === 0) return;

    viewer.camera.cancelFlight();

    const lastPt = tempPoints.pop();
    if (lastPt) {
      if (lastPt.id != null) selectedPointIds.delete(lastPt.id);
      entities.remove(lastPt);
    }

    positions.pop();

    if (positions.length < 2 && tempLineEntity) {
      entities.remove(tempLineEntity);
      tempLineEntity = null;
    }

    rebuildPointIndexMap();
  };

  const onDblClick = (e) => {
    if (stopped) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    undoLastPoint();
    viewer.camera.cancelFlight();
  };
  canvas.addEventListener("dblclick", onDblClick, true);

  const onKeyDown = (e) => {
    if (stopped) return;

    const t = e.target;
    const tag = t?.tagName?.toLowerCase?.();
    const isTyping = tag === "input" || tag === "textarea" || t?.isContentEditable;
    if (isTyping) return;

    if (e.key === "e" || e.key === "E") {
      const now = Date.now();

      if (jumpEditorOpen) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      if (now - lastEKeyTime <= DOUBLE_E_MS) {
        e.preventDefault();
        e.stopPropagation();
        lastEKeyTime = 0;
        openJumpEditor();
        return;
      }

      lastEKeyTime = now;
    }

    if (e.key === "Shift") {
      shiftDown = true;
      return;
    }

    if (e.key === "Backspace") {
      e.preventDefault();
      undoLastPoint();
    }
  };

  const onKeyUp = (e) => {
    if (stopped) return;

    if (e.key === "Shift") {
      shiftDown = false;
      applyCurveThroughSelectedPoints();
    }
  };

  const onWindowBlur = () => {
    if (stopped) return;
    if (shiftDown) {
      shiftDown = false;
      applyCurveThroughSelectedPoints();
    }
  };

  window.addEventListener("keydown", onKeyDown, true);
  window.addEventListener("keyup", onKeyUp, true);
  window.addEventListener("blur", onWindowBlur, true);

  const angleBetweenLastTwoSegmentsDeg_ENU_180IsStraight = (p0, p1, p2) => {
    Transforms.eastNorthUpToFixedFrame(p1, undefined, scratch.frame);
    Matrix4.inverse(scratch.frame, scratch.invFrame);

    const incomingW = Cartesian3.subtract(p0, p1, scratch.v1w);
    const outgoingW = Cartesian3.subtract(p2, p1, scratch.v2w);

    const incomingL = Matrix4.multiplyByPointAsVector(scratch.invFrame, incomingW, scratch.v1l);
    const outgoingL = Matrix4.multiplyByPointAsVector(scratch.invFrame, outgoingW, scratch.v2l);

    incomingL.z = 0;
    outgoingL.z = 0;

    const m1 = Cartesian3.magnitude(incomingL);
    const m2 = Cartesian3.magnitude(outgoingL);
    if (m1 < 1e-6 || m2 < 1e-6) return null;

    Cartesian3.divideByScalar(incomingL, m1, incomingL);
    Cartesian3.divideByScalar(outgoingL, m2, outgoingL);

    const dot = Cartesian3.dot(incomingL, outgoingL);
    const crossZ = incomingL.x * outgoingL.y - incomingL.y * outgoingL.x;

    const angleRad = Math.atan2(Math.abs(crossZ), dot);
    const angleDeg = CesiumMath.toDegrees(angleRad);

    return Math.max(0, Math.min(180, angleDeg));
  };

  const computeHeadingFromLastSegment = () => {
    if (positions.length < 2) return null;

    const a = positions[positions.length - 2];
    const b = positions[positions.length - 1];

    Cartesian3.subtract(b, a, scratch.segDelta);
    if (Cartesian3.magnitude(scratch.segDelta) < 1e-6) return null;

    Cartesian3.normalize(scratch.segDelta, scratch.segDirWorld);

    Transforms.eastNorthUpToFixedFrame(b, undefined, scratch.frame);
    Matrix4.inverse(scratch.frame, scratch.invFrame);

    Matrix4.multiplyByPointAsVector(scratch.invFrame, scratch.segDirWorld, scratch.segDirLocal);

    scratch.segDirLocal.z = 0;
    const m = Cartesian3.magnitude(scratch.segDirLocal);
    if (m < 1e-6) return null;

    Cartesian3.divideByScalar(scratch.segDirLocal, m, scratch.segDirLocal);

    return Math.atan2(scratch.segDirLocal.x, scratch.segDirLocal.y);
  };

  const computeMoveDistanceMeters = () => {
    const { min: baseMin, max: baseMax } = getDistanceRange();

    if (baseMin > 0 && baseMax <= 0) {
      return { dist: baseMin, baseMin, baseMax: baseMin, clampedToMin: true };
    }

    if (baseMax <= 0) {
      return { dist: 0, baseMin, baseMax, clampedToMin: false };
    }

    // default: big jump
    let dist = baseMax;

    // if heading change > 5 degrees => force 3m
    if (positions.length >= 3) {
      const p0 = positions[positions.length - 3];
      const p1 = positions[positions.length - 2];
      const p2 = positions[positions.length - 1];

      const angleDeg = angleBetweenLastTwoSegmentsDeg_ENU_180IsStraight(p0, p1, p2);
      if (angleDeg != null) {
        const deviation = 180 - angleDeg; // 0 = straight
        if (deviation > 5) dist = 3.0;
        else dist = baseMax; // keep big jump
      }
    }

    // keep existing clamps
    let clampedToMin = false;

    if (dist < baseMin) {
      dist = baseMin;
      clampedToMin = baseMin > 0;
    }
    if (dist > baseMax) dist = baseMax;

    if (baseMin > 0 && !(dist > 0)) {
      dist = baseMin;
      clampedToMin = true;
    }

    return { dist, baseMin, baseMax, clampedToMin };
  };

  const getCameraCartoSafe = () => {
    const ellipsoid = scene.globe.ellipsoid;

    const c0 = viewer.camera.positionCartographic;
    if (c0 && Number.isFinite(c0.longitude) && Number.isFinite(c0.latitude) && Number.isFinite(c0.height)) {
      scratch.camCarto.longitude = c0.longitude;
      scratch.camCarto.latitude = c0.latitude;
      scratch.camCarto.height = c0.height;
      return scratch.camCarto;
    }

    const wc = viewer.camera.positionWC;
    if (!wc) return null;

    const c1 = ellipsoid.cartesianToCartographic(wc, scratch.camCarto);
    if (c1 && Number.isFinite(c1.longitude) && Number.isFinite(c1.latitude) && Number.isFinite(c1.height)) {
      return c1;
    }

    return null;
  };

  const moveCameraByHeadingKeepHeight = (headingRad, distMeters) => {
    if (!Number.isFinite(distMeters) || distMeters <= 0) return;

    const camCarto = getCameraCartoSafe();
    if (!camCarto) return;

    const ellipsoid = scene.globe.ellipsoid;

    // short English comment: compute ENU at the ground under camera, keep camera height unchanged
    Cartesian3.fromRadians(camCarto.longitude, camCarto.latitude, 0, ellipsoid, scratch.camSurface);
    Transforms.eastNorthUpToFixedFrame(scratch.camSurface, undefined, scratch.frame);

    scratch.offsetLocal.x = Math.sin(headingRad) * distMeters; // east
    scratch.offsetLocal.y = Math.cos(headingRad) * distMeters; // north
    scratch.offsetLocal.z = 0;

    Matrix4.multiplyByPointAsVector(scratch.frame, scratch.offsetLocal, scratch.offsetWorld);
    Cartesian3.add(scratch.camSurface, scratch.offsetWorld, scratch.destSurface);

    ellipsoid.cartesianToCartographic(scratch.destSurface, scratch.destCarto);
    if (!Number.isFinite(scratch.destCarto.longitude) || !Number.isFinite(scratch.destCarto.latitude)) {
      return;
    }

    scratch.destCarto.height = camCarto.height;
    ellipsoid.cartographicToCartesian(scratch.destCarto, scratch.dest);

    const cur = viewer.camera.positionWC;
    const moveLen = cur ? Cartesian3.distance(cur, scratch.dest) : distMeters;

    viewer.camera.cancelFlight();

    const orientation = {
      heading: viewer.camera.heading,
      pitch: viewer.camera.pitch,
      roll: viewer.camera.roll,
    };

    // short English comment: tiny moves should be instant (flyTo may ignore very small deltas)
    const forceInstant = distMeters <= 15 || moveLen <= 15;

    const durMs = Number(getDuration?.() ?? 0);
    const durSec = Number.isFinite(durMs) ? Math.max(0, durMs) / 1000 : 0;

    if (forceInstant || durSec <= 0) {
      viewer.camera.setView({ destination: scratch.dest, orientation });
    } else {
      viewer.camera.flyTo({ destination: scratch.dest, duration: durSec, orientation });
    }
  };

  const moveCameraAlongLastSegment = () => {
    if (positions.length < 2) return;

    const { dist } = computeMoveDistanceMeters();
    if (!(dist > 0)) return;

    let headingRad = computeHeadingFromLastSegment();
    if (Number.isFinite(headingRad)) {
      lastHeadingRad = headingRad;
    } else if (Number.isFinite(lastHeadingRad)) {
      headingRad = lastHeadingRad;
    } else {
      headingRad = viewer.camera.heading;
      lastHeadingRad = headingRad;
    }

    moveCameraByHeadingKeepHeight(headingRad, dist);
  };

  const buildGeojsonFromPositions = (worldPositions) => {
    const coords = worldPositions.map((p) => {
      const c = Cartographic.fromCartesian(p);
      return [CesiumMath.toDegrees(c.longitude), CesiumMath.toDegrees(c.latitude), c.height || 0];
    });

    return {
      type: "Feature",
      geometry: { type: "LineString", coordinates: coords },
      properties: {},
    };
  };

  const pickPointEntityNearScreen = (screenPos, maxPx) => {
    let best = null;
    let bestD2 = maxPx * maxPx;

    for (let i = 0; i < tempPoints.length; i++) {
      const w = positions[i];
      if (!w) continue;

      const win = toWin(w, scratch.w2);
      if (!win) continue;

      const dx = win.x - screenPos.x;
      const dy = win.y - screenPos.y;
      const d2 = dx * dx + dy * dy;

      if (d2 <= bestD2) {
        bestD2 = d2;
        best = tempPoints[i];
      }
    }

    return best;
  };

  const tryToggleSelectionAt = (screenPos) => {
    const sp = clampScreenToCanvas(screenPos, scratch.sp);

    const picked = scene.pick(sp);
    const ent = picked?.id;

    if (ent && ent.id != null && indexByPointId.has(ent.id)) {
      if (selectedPointIds.has(ent.id)) {
        selectedPointIds.delete(ent.id);
        setPointSelectedVisual(ent, false);
      } else {
        selectedPointIds.add(ent.id);
        setPointSelectedVisual(ent, true);
      }
      return true;
    }

    const nearest = pickPointEntityNearScreen(sp, PICK_MAX_PX);
    if (!nearest || nearest.id == null) return false;

    if (selectedPointIds.has(nearest.id)) {
      selectedPointIds.delete(nearest.id);
      setPointSelectedVisual(nearest, false);
    } else {
      selectedPointIds.add(nearest.id);
      setPointSelectedVisual(nearest, true);
    }

    return true;
  };

  const hermitePoint = (p1, p2, m1, m2, t, out) => {
    const t2 = t * t;
    const t3 = t2 * t;

    const h00 = 2 * t3 - 3 * t2 + 1;
    const h10 = t3 - 2 * t2 + t;
    const h01 = -2 * t3 + 3 * t2;
    const h11 = t3 - t2;

    out.x = h00 * p1.x + h10 * m1.x + h01 * p2.x + h11 * m2.x;
    out.y = h00 * p1.y + h10 * m1.y + h01 * p2.y + h11 * m2.y;
    out.z = h00 * p1.z + h10 * m1.z + h01 * p2.z + h11 * m2.z;
    return out;
  };

  const buildCurveThroughPointsWorld = (ptsWorld, spacingMeters, smoothness01) => {
    if (!Array.isArray(ptsWorld) || ptsWorld.length < 2) return null;

    const ref = ptsWorld[0];
    Transforms.eastNorthUpToFixedFrame(ref, undefined, scratch.frame);
    Matrix4.inverse(scratch.frame, scratch.invFrame);

    const ptsL = ptsWorld.map((p) => Matrix4.multiplyByPoint(scratch.invFrame, p, new Cartesian3()));

    if (smoothness01 <= 0.01) {
      const outWorld = [];
      outWorld.push(Cartesian3.clone(ptsWorld[0], new Cartesian3()));

      for (let i = 0; i < ptsWorld.length - 1; i++) {
        const aW = ptsWorld[i];
        const bW = ptsWorld[i + 1];
        const d = Cartesian3.distance(aW, bW);
        const steps = Math.max(1, Math.ceil(d / spacingMeters));

        for (let k = 1; k < steps; k++) {
          const t = k / steps;
          outWorld.push(Cartesian3.lerp(aW, bW, t, new Cartesian3()));
        }
        outWorld.push(Cartesian3.clone(bW, new Cartesian3()));
      }

      return outWorld;
    }

    const tangents = new Array(ptsL.length);
    const s = smoothness01;

    for (let i = 0; i < ptsL.length; i++) {
      const m = new Cartesian3();

      if (i === 0) {
        Cartesian3.subtract(ptsL[1], ptsL[0], m);
      } else if (i === ptsL.length - 1) {
        Cartesian3.subtract(ptsL[i], ptsL[i - 1], m);
      } else {
        Cartesian3.subtract(ptsL[i + 1], ptsL[i - 1], m);
        Cartesian3.multiplyByScalar(m, 0.5, m);
      }

      Cartesian3.multiplyByScalar(m, s, m);
      tangents[i] = m;
    }

    const curveL = [];
    curveL.push(Cartesian3.clone(ptsL[0], new Cartesian3()));

    const maxTotal = 8000;
    for (let i = 0; i < ptsL.length - 1; i++) {
      const p1 = ptsL[i];
      const p2 = ptsL[i + 1];
      const m1 = tangents[i];
      const m2 = tangents[i + 1];

      const d = Cartesian3.distance(p1, p2);
      let steps = Math.max(1, Math.ceil(d / spacingMeters));

      if (curveL.length + steps > maxTotal) {
        steps = Math.max(1, Math.floor((maxTotal - curveL.length) / 1));
      }

      for (let k = 1; k < steps; k++) {
        const t = k / steps;
        curveL.push(hermitePoint(p1, p2, m1, m2, t, new Cartesian3()));
      }

      curveL.push(Cartesian3.clone(p2, new Cartesian3()));
      if (curveL.length >= maxTotal) break;
    }

    return curveL.map((pL) => Matrix4.multiplyByPoint(scratch.frame, pL, new Cartesian3()));
  };

  function applyCurveThroughSelectedPoints() {
    if (stopped) return;

    if (selectedPointIds.size < 2) {
      clearSelection();
      return;
    }

    const idxs = [];
    for (const id of selectedPointIds) {
      const idx = indexByPointId.get(id);
      if (Number.isFinite(idx)) idxs.push(idx);
    }
    idxs.sort((a, b) => a - b);

    const orderedIdxs = [];
    for (let i = 0; i < idxs.length; i++) {
      if (i === 0 || idxs[i] !== idxs[i - 1]) orderedIdxs.push(idxs[i]);
    }
    if (orderedIdxs.length < 2) {
      clearSelection();
      return;
    }

    const startIdx = orderedIdxs[0];
    const endIdx = orderedIdxs[orderedIdxs.length - 1];
    if (startIdx < 0 || endIdx >= positions.length || startIdx > endIdx) {
      clearSelection();
      return;
    }

    const ptsWorld = orderedIdxs.map((i) => positions[i]);
    const spacing = getCurveSpacingMeters(ptsWorld);
    const smoothness = getSmoothness01();

    const curveWorld = buildCurveThroughPointsWorld(ptsWorld, spacing, smoothness);
    if (!curveWorld || curveWorld.length < 2) {
      clearSelection();
      return;
    }

    positions.splice(startIdx, endIdx - startIdx + 1, ...curveWorld);

    rebuildAllPointEntities();
    ensureTempLine();
    refreshTempLineBinding();
  }

  // Shift+click: select/toggle points
  handler.setInputAction(
    (movement) => {
      if (stopped) return;
      if (!movement?.position) return;
      const changed = tryToggleSelectionAt(movement.position);
      if (changed) syncSelectionVisuals();
    },
    ScreenSpaceEventType.LEFT_CLICK,
    KeyboardEventModifier.SHIFT,
  );

  // Normal click: add points
  handler.setInputAction((movement) => {
    if (stopped) return;
    if (!movement?.position) return;

    if (shiftDown) return;

    const now = performance.now();
    const dt = now - lastLeftClickTime;
    const dp = Cartesian2.distance(movement.position, lastLeftClickScreen);

    lastLeftClickTime = now;
    Cartesian2.clone(movement.position, lastLeftClickScreen);

    if (dt < DOUBLE_CLICK_MS && dp < DOUBLE_CLICK_PX) return;

    const pos = getMapPosition(movement);
    if (!pos) return;

    positions.push(pos);

    const pt = entities.add({
      position: pos,
      point: {
        pixelSize: 10,
        color: Color.YELLOW,
        outlineColor: Color.BLACK,
        outlineWidth: 1,
        disableDepthTestDistance: Number.POSITIVE_INFINITY,
      },
      properties: makeHelperProps(),
    });
    tempPoints.push(pt);

    rebuildPointIndexMap();
    ensureTempLine();
    refreshTempLineBinding();

    const centered = centerCameraOnCommitted(pos);
if (!centered && positions.length >= 2) moveCameraAlongLastSegment();
  }, ScreenSpaceEventType.LEFT_CLICK);

  handler.setInputAction(() => {}, ScreenSpaceEventType.LEFT_DOUBLE_CLICK);
  handler.setInputAction((movement) => {
    if (stopped) return;
    const p = movement?.endPosition;
    if (!p) return;

    edgeAutoPan.setMousePosition(p);
    edgeAutoPan.start();
  }, ScreenSpaceEventType.MOUSE_MOVE);

  handler.setInputAction(() => {
    if (stopped) return;
    const out = stop();
    onStop?.(out);
  }, ScreenSpaceEventType.RIGHT_CLICK);

  const cleanup = () => {
    window.removeEventListener("keydown", onKeyDown, true);
    window.removeEventListener("keyup", onKeyUp, true);
    window.removeEventListener("blur", onWindowBlur, true);
    canvas.removeEventListener("dblclick", onDblClick, true);
    edgeAutoPan.destroy();
    centerPanToken++;

    viewer.camera.cancelFlight();

    try {
      handler.destroy();
    } catch (_) {}

    for (const p of tempPoints) entities.remove(p);
    tempPoints.length = 0;

    indexByPointId.clear();
    selectedPointIds.clear();

    if (tempLineEntity) entities.remove(tempLineEntity);
    tempLineEntity = null;
  };

  const stop = () => {
    if (stopped) return null;
    stopped = true;

    const out = positions.length >= 2 ? { geojson: buildGeojsonFromPositions(positions.slice()) } : null;

    cleanup();
    return out;
  };

  const cancel = () => {
    if (stopped) return;
    stopped = true;
    cleanup();
  };

  return { stop, cancel };
}
